/**
 * Leaf cell for the Source ▸ Leads list tree: list name + performance tags from
 * the list tag rules (same rules/colors as the Lists tab), details on hover.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import type { ListPerformanceTagsResponse } from '@/hooks';
import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import {
  StatsHoverPopover,
  StatsTooltipTagsBlock,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/StatsHoverPopover';
import {
  resolveTagDetailsForListLeaf,
  resolveTagsForListLeaf,
} from '@/features/smswiser-campaigns/components/ListsStatsTab/utils';

interface SourceListLeafCellProps {
  row: { leafId?: string; label?: string };
  /** Response of list-performance-tags (list rules already executed by the backend). */
  listTags?: ListPerformanceTagsResponse;
  leadsLists: Array<{ id: number; name?: string; voluumId?: string | null }>;
  tagsLoading?: boolean;
}

export const SourceListLeafCell: FC<SourceListLeafCellProps> = ({
  row,
  listTags,
  leadsLists,
  tagsLoading = false,
}) => {
  const tags = resolveTagsForListLeaf(
    row,
    listTags?.tagsByListId ?? {},
    listTags?.tagsByWiserId ?? {},
    leadsLists,
  );
  const label = (
    <span className="block min-w-0 truncate text-sm text-foreground">
      {row.label || '—'}
    </span>
  );
  if (tags.length === 0 && !tagsLoading) return label;

  const content = (
    <span className="flex min-w-0 items-center gap-1.5">
      {label}
      <LeadsListTags
        performanceTags={tags}
        tagColors={listTags?.tagColors}
        className="inline-flex shrink-0"
        compact
        disableTooltip
        loading={tagsLoading}
      />
    </span>
  );
  if (tags.length === 0) return content;

  return (
    <StatsHoverPopover
      content={
        <StatsTooltipTagsBlock
          performanceTags={tags}
          performanceTagDetails={resolveTagDetailsForListLeaf(
            row,
            listTags?.tagDetailsByListId ?? {},
            leadsLists,
          )}
          tagColors={listTags?.tagColors}
        />
      }
    >
      {content}
    </StatsHoverPopover>
  );
};
